import { useEffect, useRef, useState } from "react";
import { AxiosInstance } from "axios";
import {
	ItemImageRole,
	resolveItemImageUrl,
} from "../util/itemImageCache";

type Options = {
	/** Default SVG / placeholder shown immediately */
	placeholder: string;
	role?: ItemImageRole;
	/** Only load when element is near viewport (default true) */
	lazy?: boolean;
	api?: AxiosInstance | null;
	/** Skip network/cache (e.g. invalid id) */
	enabled?: boolean;
};

/**
 * Lazy-load item images: placeholder first → IDB cache → server fetch + hash store.
 */
export function useCachedItemImage(
	itemId: number | null | undefined,
	options: Options,
) {
	const {
		placeholder,
		role = "main",
		lazy = true,
		api = null,
		enabled = true,
	} = options;

	const [src, setSrc] = useState(placeholder);
	const [isPlaceholder, setIsPlaceholder] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const imgRef = useRef<HTMLElement | null>(null);
	const [visible, setVisible] = useState(!lazy);
	const requestGen = useRef(0);

	// Observe visibility for lazy load
	useEffect(() => {
		if (!lazy) {
			setVisible(true);
			return;
		}
		const el = imgRef.current;
		if (!el || typeof IntersectionObserver === "undefined") {
			setVisible(true);
			return;
		}

		const obs = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting)) {
					setVisible(true);
					obs.disconnect();
				}
			},
			{ rootMargin: "120px", threshold: 0.01 },
		);
		obs.observe(el);
		return () => obs.disconnect();
	}, [lazy, itemId]);

	// Reset to placeholder when item changes
	useEffect(() => {
		setSrc(placeholder);
		setIsPlaceholder(true);
		setIsLoading(false);
		if (lazy) setVisible(false);
	}, [itemId, role, placeholder, lazy]);

	// Resolve cache / network when visible
	useEffect(() => {
		if (!enabled || !itemId || itemId < 1 || !visible) return;

		const gen = ++requestGen.current;
		let cancelled = false;

		const run = async () => {
			setIsLoading(true);
			try {
				const { url } = await resolveItemImageUrl(itemId, role, api ?? undefined);
				if (cancelled || gen !== requestGen.current) return;
				if (url) {
					setSrc(url);
					setIsPlaceholder(false);
				} else {
					setSrc(placeholder);
					setIsPlaceholder(true);
				}
			} catch {
				if (!cancelled && gen === requestGen.current) {
					setSrc(placeholder);
					setIsPlaceholder(true);
				}
			} finally {
				if (!cancelled && gen === requestGen.current) {
					setIsLoading(false);
				}
			}
		};

		void run();
		return () => {
			cancelled = true;
		};
	}, [itemId, role, visible, enabled, api, placeholder]);

	return {
		src,
		isPlaceholder,
		isLoading,
		/** Attach to the image/container for IntersectionObserver */
		imgRef,
	};
}
